import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Puzzle, Building2, Lightbulb, Code2, Workflow, GraduationCap, CheckCircle2 } from 'lucide-react';
import { WaitlistCTA } from '../components/WaitlistCTA';
const SERVICES = [{
  icon: Lightbulb,
  title: 'AI Strategy & Roadmap',
  description: 'We assess your current operations and identify high-impact opportunities for AI automation, delivering a clear, phased implementation roadmap.'
}, {
  icon: Code2,
  title: 'Custom Implementation',
  description: 'Our engineers build and deploy bespoke AI solutions tailored to your unique workflows, ensuring seamless integration with your existing systems.'
}, {
  icon: Workflow,
  title: 'Workflow Automation',
  description: 'We connect your disparate tools and automate complex, multi-step processes, eliminating manual data entry and reducing operational friction.'
}, {
  icon: GraduationCap,
  title: 'Training & Enablement',
  description: "We don't just hand over the keys. We train your team on how to leverage new AI capabilities effectively, ensuring long-term adoption and success."
}];
const APPROACH_STEPS = [{
  title: 'Discovery',
  description: 'Deep dive into your business processes, pain points, and goals.'
}, {
  title: 'Design',
  description: 'Architecting a scalable, secure AI solution tailored to your needs.'
}, {
  title: 'Deployment',
  description: 'Agile implementation with minimal disruption to your daily operations.'
}, {
  title: 'Optimization',
  description: 'Continuous monitoring, tuning, and refinement for maximum ROI.'
}];
export function Consulting() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-sans pt-24 md:pt-32 pb-20 overflow-hidden" data-id="element-7695" data-mp="2ixsfpmdkkqty">
      {/* Hero Section */}
      <section className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-10 md:pt-20 pb-24 md:pb-32" data-id="element-7696" data-mp="2nxx03nhlm5pg">
        {/* Faint floating particles background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-7697" data-mp="ssnwkbvfd7y5">
          {Array.from({
          length: 20
        }).map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-[#EA580C]/20" style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }} animate={{
          y: [0, -20, 0],
          opacity: [0.2, 0.5, 0.2]
        }} transition={{
          duration: 3 + Math.random() * 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: Math.random() * 2
        }} data-id="element-7698" data-mp="ns18wu6bgel8" data-mpi={i} />)}
        </div>

        <div className="relative z-10 max-w-3xl" data-id="element-7699" data-mp="1lgtfqu6tyovv">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-7700" data-mp="1lfibkt9h3f48">
            <span className="inline-block text-xs font-bold tracking-[2px] uppercase text-[#EA580C] mb-6" data-id="element-7701" data-mp="3nbbz84jswg9e">
              AI Consulting Services
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-title font-medium tracking-tight leading-[1.05] text-[#2B211A] mb-6" data-id="element-7702" data-mp="1xtf19y2tx1ws">
              AI Consulting Services That <br className="hidden md:block" data-id="element-7703" data-mp="1o2kcaly77o7n" />
              <span className="text-[#EA580C]" data-id="element-7704" data-mp="1o1rfacp1kd2p">Drive Real Results</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-2xl mb-10" data-id="element-7705" data-mp="1t02oqmbfclke">
              From strategy to implementation, we help organizations transform
              their operations with AI-powered analytics, custom solutions, and
              hands-on expertise.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-14" data-id="element-7706" data-mp="154zozjb30xp5">
              <WaitlistCTA className="group inline-flex items-center justify-center gap-2 rounded-md bg-[#EA580C] px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d24e09]" data-id="element-7707" data-mp="1a3b1y2wobobl">
                Schedule a Consultation
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" data-id="element-7708" data-mp="14eaypzawgt77" />
              </WaitlistCTA>
              <button className="inline-flex items-center justify-center rounded-md bg-white border border-neutral-200 px-6 py-3.5 text-sm font-semibold text-[#2B211A] shadow-sm transition-colors hover:bg-neutral-50" data-id="element-7709" data-mp="10cg2h97tt60a">
                View Solutions
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-3 md:gap-4" data-id="element-7710" data-mp="lg8fk1519qhb">
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-2 shadow-sm" data-id="element-7711" data-mp="gogntna55epl">
                <ShieldCheck size={16} className="text-[#EA580C]" data-id="element-7712" data-mp="cnmyqqkyptv4" />
                <span className="text-xs font-semibold text-[#2B211A]" data-id="element-7713" data-mp="7otor84vwkzp">
                  SOC 2 Controls in Place
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-2 shadow-sm" data-id="element-7714" data-mp="2qs5am3jqjnp">
                <Puzzle size={16} className="text-[#EA580C]" data-id="element-7715" data-mp="3jrtmgvni5rws" />
                <span className="text-xs font-semibold text-[#2B211A]" data-id="element-7716" data-mp="3jto86c6llo1x">
                  120+ Integrations
                </span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 px-4 py-2 shadow-sm" data-id="element-7717" data-mp="3tqj5wsv1rkf5">
                <Building2 size={16} className="text-[#EA580C]" data-id="element-7718" data-mp="2if0xp6t6dji" />
                <span className="text-xs font-semibold text-[#2B211A]" data-id="element-7719" data-mp="311vf6hrj42u7">
                  Enterprise Ready
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-24 border-y border-neutral-200/70" data-id="element-7720" data-mp="3ax5h75hxga7z">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-7721" data-mp="3fr9ojpv3uk9l">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-7722" data-mp="2ib49bq6r2ukk">
            <h2 className="text-3xl md:text-4xl font-title font-medium tracking-tight text-[#2B211A] mb-4" data-id="element-7723" data-mp="2i8i07m47o708">
              Comprehensive AI Solutions
            </h2>
            <p className="text-lg text-neutral-600" data-id="element-7724" data-mp="2f757kalplad9">
              We provide end-to-end consulting services to help you navigate the
              complexities of AI adoption and drive measurable business value.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-7725" data-mp="2jzpx2zxl11az">
            {SERVICES.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-[#FAF8F4] rounded-2xl p-8 border border-neutral-200/60" data-id="element-7726" data-mp="1w9lt2rghezjg" data-mpi={index}>
                <div className="w-12 h-12 rounded-xl bg-white border border-neutral-200 flex items-center justify-center mb-6 shadow-sm" data-id="element-7727" data-mp="3hmoxnlreate5">
                  <service.icon size={24} className="text-[#EA580C]" data-id="element-7728" data-mp="3cnlr1wvrof42" />
                </div>
                <h3 className="text-xl font-bold text-[#2B211A] mb-3" data-id="element-7729" data-mp="3k6v17sc3khu6">
                  {service.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed" data-id="element-7730" data-mp="3p4mrkhx3fvw9">
                  {service.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24" data-id="element-7731" data-mp="6sq84v036hr7">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-7732" data-mp="6trlj64krgc3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-7733" data-mp="brtdkgjzapzp">
            <div data-id="element-7734" data-mp="btnqp8p0ddt8">
              <h2 className="text-3xl md:text-4xl font-title font-medium tracking-tight text-[#2B211A] mb-6" data-id="element-7735" data-mp="3u62dec73i6fp">
                Our Proven Approach
              </h2>
              <p className="text-lg text-neutral-600 mb-8 leading-relaxed" data-id="element-7736" data-mp="206z8bccgasq">
                We don't believe in one-size-fits-all solutions. Our methodology
                is rooted in understanding your specific business context and
                delivering tailored AI implementations that generate real ROI.
              </p>
              <WaitlistCTA className="inline-flex items-center justify-center rounded-full bg-[#2B211A] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-black" data-id="element-7737" data-mp="10y76lr0g3eh">
                Learn More About Our Process
              </WaitlistCTA>
            </div>

            <div className="space-y-6" data-id="element-7738" data-mp="3n6bmjvbsxrzk">
              {APPROACH_STEPS.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="flex gap-4" data-id="element-7739" data-mp="opr8yyubd5h0" data-mpi={index}>
                  <div className="flex-shrink-0 mt-1" data-id="element-7740" data-mp="2zj3lxqpbj73v">
                    <div className="w-8 h-8 rounded-full bg-[#EA580C]/10 flex items-center justify-center text-[#EA580C] font-bold text-sm" data-id="element-7741" data-mp="2zghd7nqoylk2">
                      {index + 1}
                    </div>
                  </div>
                  <div data-id="element-7742" data-mp="1f09tt6w5i30a">
                    <h4 className="text-lg font-bold text-[#2B211A] mb-1" data-id="element-7743" data-mp="1ezrqxrezgqje">
                      {step.title}
                    </h4>
                    <p className="text-neutral-600" data-id="element-7744" data-mp="1jybbbndxhu9n">{step.description}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-10" data-id="element-7745" data-mp="1trifj4wxn485">
        <div className="bg-[#1A1410] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" data-id="element-7746" data-mp="1xppt5qw3npf4">
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(circle at 50% 0%, rgba(234,88,12,0.15) 0%, transparent 70%)'
        }} data-id="element-7747" data-mp="22rf5d9v2694s" />

          <div className="relative z-10 max-w-2xl mx-auto" data-id="element-7748" data-mp="8d4j995ult2p">
            <h2 className="text-3xl md:text-5xl font-title font-medium text-white tracking-tight leading-[1.1] mb-6" data-id="element-7749" data-mp="db5yfj05l88w">
              Ready to transform your business with AI?
            </h2>
            <p className="text-lg text-neutral-400 mb-10" data-id="element-7750" data-mp="1enbbl5gvhk48">
              Let's discuss your challenges and explore how our consulting team
              can help you build a smarter, more efficient organization.
            </p>
            <WaitlistCTA className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#EA580C] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#EA580C]/20 transition-all hover:bg-[#d24e09] hover:scale-105" data-id="element-7751" data-mp="1no9ibvip45qi">
              Book Your Free Consultation
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" data-id="element-7752" data-mp="22d5oto53tap0" />
            </WaitlistCTA>
          </div>
        </div>
      </section>
    </div>;
}